import axios from 'axios';
import {
  CREATE_SIGNING_WORKFLOW_URL,
  LIST_WORKFLOW_MANAGEMENT_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  extract_error_message,
  parse_response_data,
} from '@/utils/api_response_helpers';
import {
  create_empty_step,
  normalize_workflow,
} from './esigning_workflows_helpers';

const esigning_workflows_methods = {
  async fetch_workflows() {
    this.workflows_loading = true;

    try {
      const response = await axios.get(LIST_WORKFLOW_MANAGEMENT_URL, get_request_config());
      const parsed = parse_response_data(response);
      const workflows = Array.isArray(parsed?.workflows) ? parsed.workflows : [];

      this.workflows = workflows
        .map(normalize_workflow)
        .filter(Boolean);
    } catch (error) {
      this.workflows = [];
      this.show_error(extract_error_message(error, 'Failed to load workflows.'));
    } finally {
      this.workflows_loading = false;
    }
  },

  get_selected_query_entity_type_option() {
    const query = this.$route?.query || {};
    const id = String(query.entity_type_id || '').trim();
    const name = String(query.entity_type_name || '').trim();

    if (!id && !name) {
      return null;
    }

    return {
      value: id || name,
      label: name || id,
    };
  },

  map_entity_type_option(entity_type, index = 0) {
    const value = entity_type?.id ?? entity_type?.entity_type_id ?? entity_type?.value;
    const label = entity_type?.entity_type_name || entity_type?.name || entity_type?.label;

    if (value == null || !String(label || '').trim()) {
      return null;
    }

    return {
      value: String(value),
      label: String(label).trim() || `Entity Type ${index + 1}`,
    };
  },

  async fetch_entity_types() {
    this.entity_types_loading = true;

    try {
      const response = await axios.get(VIEW_ENTITY_TYPE_URL, get_request_config());
      const parsed = parse_response_data(response);
      const entity_types = Array.isArray(parsed?.data) ? parsed.data : [];

      this.entity_types = entity_types;
      this.apply_default_entity_type_selection();
    } catch (error) {
      this.entity_types = [];
      this.show_error(extract_error_message(error, 'Failed to load entity types.'));
    } finally {
      this.entity_types_loading = false;
    }
  },

  apply_default_entity_type_selection() {
    const default_option = this.get_selected_query_entity_type_option() || this.entity_type_options[0];

    if (!default_option?.value) {
      return;
    }

    this.workflow_steps = this.workflow_steps.map((step) => (
      step.entity_type_id
        ? step
        : { ...step, entity_type_id: String(default_option.value) }
    ));
  },

  open_create_workflow_modal() {
    this.reset_form();
  },

  reset_form() {
    this.workflow_form = {
      name: '',
      description: '',
    };
    this.workflow_steps = [create_empty_step()];
    this.apply_default_entity_type_selection();
  },

  add_step() {
    const next_step = create_empty_step(this.workflow_steps.length + 1);
    const default_option = this.get_selected_query_entity_type_option() || this.entity_type_options[0];

    if (default_option?.value) {
      next_step.entity_type_id = String(default_option.value);
    }

    this.workflow_steps.push(next_step);
  },

  remove_step(index) {
    this.workflow_steps.splice(index, 1);

    if (!this.workflow_steps.length) {
      this.workflow_steps = [create_empty_step()];
      return;
    }

    this.workflow_steps = this.workflow_steps.map((step, step_index) => ({
      ...step,
      order: step_index + 1,
    }));
  },

  build_steps_payload() {
    return this.workflow_steps
      .map((step, index) => ({
        entity_type_id: Number(step.entity_type_id) || null,
        order: index + 1,
        expected_signer_count: Number(step.expected_signer_count) || 1,
      }))
      .filter((step) => step.entity_type_id);
  },

  validate_workflow_form(steps) {
    if (!this.workflow_form.name.trim()) {
      return 'Please enter a workflow name.';
    }

    if (!steps.length || steps.length !== this.workflow_steps.length) {
      return 'Please select an entity type for every step.';
    }

    return '';
  },

  workflow_step_summary(workflow) {
    const step_count = Number(workflow?.step_count || 0) || 0;
    const signer_count = Number(workflow?.total_expected_signers || 0) || 0;
    const step_label = `${step_count} step${step_count === 1 ? '' : 's'}`;
    const signer_label = `${signer_count} signer${signer_count === 1 ? '' : 's'}`;

    return `${step_label} . ${signer_label}`;
  },

  // workflow_template_summary(workflow) {
  //   const assigned_count = Number(workflow?.assigned_template_count || 0) || 0;

  //   if (!assigned_count) {
  //     return 'Not assigned';
  //   }

  //   return `${assigned_count} template${assigned_count === 1 ? '' : 's'} assigned`;
  // },

  workflow_entity_type_summary(workflow) {
    const step_names = (Array.isArray(workflow?.steps) ? workflow.steps : [])
      .map((step) => String(step?.entity_type_name || '').trim())
      .filter(Boolean);

    if (!step_names.length) {
      return workflow?.description || 'No entity types assigned.';
    }

    return step_names.join(', ');
  },

  format_date(value) {
    const date = value ? new Date(value) : null;

    if (!date || Number.isNaN(date.getTime())) {
      return 'Unknown date';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  },

  async create_workflow() {
    if (this.workflow_running) return;

    const steps = this.build_steps_payload();
    const validation_message = this.validate_workflow_form(steps);

    if (validation_message) {
      this.show_error(validation_message);
      return;
    }

    this.workflow_running = true;
    const loader = await this.show_loader('Creating workflow...');

    try {
      const response = await axios.post(
        CREATE_SIGNING_WORKFLOW_URL,
        {
          name: this.workflow_form.name.trim(),
          description: this.workflow_form.description.trim(),
          steps,
        },
        get_request_config(),
      );

      const parsed = parse_response_data(response);

      if (parsed?.status !== 'success') {
        this.show_error(parsed?.message || 'Failed to create workflow.');
        return;
      }

      this.show_success(parsed?.message || 'Workflow successfully created.');
      await this.fetch_workflows();
      this.close_modal('create_esigning_workflow_modal');
      this.reset_form();
    } catch (error) {
      this.show_error(
        extract_error_message(error, 'Failed to create workflow.'),
      );
    } finally {
      await this.close_loader(loader);
      this.workflow_running = false;
    }
  },
};

export default esigning_workflows_methods;
